/* ============================================
   ORBITAL SYSTEM COMPONENT
   The Creative Orchestra - GEMINI Framework
   Complete orbital system with work orbs and center
   ============================================ */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import WorkOrb from './WorkOrb';
import CenterOrb from './CenterOrb';
import { WORKS_ORDER, ORBITAL_CONFIG } from '@utils/constants';

/**
 * Orbital System Component
 * The complete orbital display with:
 * - Center orb (novel)
 * - 8 work orbs orbiting around
 * - Orbital rings/paths
 * - Rotating animation
 */
const OrbitalSystem = ({
  centerOrbSize = ORBITAL_CONFIG.centerOrbSize || 140,
  workOrbSize = ORBITAL_CONFIG.workOrbSize || 80,
  orbitRadius = ORBITAL_CONFIG.orbitRadius || 280,
  orbitDuration = ORBITAL_CONFIG.orbitDuration || 120, // seconds for full rotation
  enableRotation = true,
  className = '',
}) => {
  // Calculate positions for each work orb
  const workPositions = useMemo(() => {
    return WORKS_ORDER.map((workId, index) => {
      const angle = (index / WORKS_ORDER.length) * Math.PI * 2 - Math.PI / 2;
      return {
        workId,
        angle,
        index,
      };
    });
  }, []);

  return (
    <div 
      className={`relative ${className}`}
      style={{
        width: (orbitRadius + workOrbSize) * 2,
        height: (orbitRadius + workOrbSize) * 2,
      }}
    >
      {/* Outer decorative ring */}
      <motion.div
        className="absolute inset-0 rounded-full border border-white/5"
        style={{ margin: workOrbSize / 2 }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
      />

      {/* Main orbit path */}
      <motion.div
        className="absolute rounded-full border border-white/10"
        style={{
          left: '50%',
          top: '50%',
          width: orbitRadius * 2,
          height: orbitRadius * 2,
          marginLeft: -orbitRadius,
          marginTop: -orbitRadius,
        }}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      />

      {/* Inner decorative ring */}
      <motion.div
        className="absolute rounded-full border border-white/5"
        style={{
          left: '50%',
          top: '50%',
          width: orbitRadius * 1.4,
          height: orbitRadius * 1.4,
          marginLeft: -orbitRadius * 0.7,
          marginTop: -orbitRadius * 0.7,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      />

      {/* Rotating container for work orbs */}
      <motion.div
        className="absolute inset-0"
        animate={enableRotation ? { rotate: 360 } : {}}
        transition={{
          duration: orbitDuration,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        {/* Work orbs */}
        {workPositions.map(({ workId, angle, index }) => (
          <motion.div
            key={workId}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
            }}
            // Counter-rotate to keep orbs upright
            animate={enableRotation ? { rotate: -360 } : {}}
            transition={{
              duration: orbitDuration,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <WorkOrb
              workId={workId}
              angle={angle}
              radius={orbitRadius}
              size={workOrbSize}
              index={index}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Center orb (static, not rotating) */}
      <CenterOrb size={centerOrbSize} />

      {/* Stage labels */}
      <StageLabels orbitRadius={orbitRadius} />
    </div>
  );
};

/**
 * Stage Labels Component
 * Shows GEMINI stage labels around the orbit
 */
const StageLabels = ({ orbitRadius }) => {
  const stages = [
    { label: 'G', fullName: 'Generation', angle: -Math.PI / 2 - 0.3 },
    { label: 'E', fullName: 'Evaluation', angle: -0.1 },
    { label: 'M', fullName: 'Management', angle: Math.PI / 2 - 0.2 },
    { label: 'IN', fullName: 'INspiration', angle: Math.PI + 0.3 },
  ];

  return (
    <>
      {stages.map((stage, index) => {
        const labelRadius = orbitRadius + 70;
        const x = Math.cos(stage.angle) * labelRadius;
        const y = Math.sin(stage.angle) * labelRadius;

        return (
          <motion.div
            key={stage.label}
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 + index * 0.1, duration: 0.5 }}
          >
            <div className="text-center">
              <span className="text-xs font-bold text-white/30 tracking-wider">
                {stage.label}
              </span>
            </div>
          </motion.div>
        );
      })}
    </>
  );
};

/**
 * Static Orbital Display (no rotation)
 * For use in smaller viewports or simpler displays
 */
export const StaticOrbitalDisplay = ({
  centerOrbSize = 100,
  workOrbSize = 60,
  orbitRadius = 180,
  className = '',
}) => {
  return (
    <OrbitalSystem
      centerOrbSize={centerOrbSize}
      workOrbSize={workOrbSize}
      orbitRadius={orbitRadius}
      enableRotation={false}
      className={className}
    />
  );
};

/**
 * Mini Orbital System (for mobile or compact displays)
 */
export const MiniOrbitalSystem = ({ className = '' }) => {
  return (
    <OrbitalSystem
      centerOrbSize={80}
      workOrbSize={45}
      orbitRadius={140}
      orbitDuration={90}
      className={className}
    />
  );
};

export default OrbitalSystem;
